import React, { useMemo, useState } from 'react';
import { InfoAlert } from 'src/components/atoms/alerts/InfoAlert';
import {
  DataBox,
  DataBoxBaseProps,
  DataBoxLegendProps,
} from 'src/components/molecules/DataBox';
import { NewBarLineGraph } from 'src/components/molecules/graphs/NewBarLineGraph';
import { useAggregationLevelContext } from 'src/features/MM/contexts/AggregationLevelContext';
import { useGetDateAxisFormatter } from 'src/features/MM/hooks/useGetDateAxisFormatter';
import { getAggregateLevelData } from 'src/features/MM/utils/getAggregateLevelData';
import { useChartEffectTickFormatter } from 'src/hooks/useEffectFormatter';
import { sortEpoch } from 'src/utils/date';
import {
  incrementalMetricDef,
  observedMetricDef,
  predictedMetricDef,
} from 'src/utils/metricDefinitions';
import { useMMDataContext } from '../../../contexts/MMDataContext';
import { TimeseriesDataTableModal } from '../../molecules/TimeseriesDataTableModal';
import { useKpiMetricLabels } from 'src/hooks/useKpiMetricLabels';

type Datapoint = { utcEpoch: number } & Record<string, number>;

const xAxisKey = 'utcEpoch';

export const DecompositionsBox: React.FC<DataBoxBaseProps> = ({ ...props }) => {
  const { decomps, sales, predictedSales } = useMMDataContext();
  const { aggregateLevel } = useAggregationLevelContext();
  const { metricLabel, incrementalLabel, predictedLabel } =
    useKpiMetricLabels();

  const legendProps: DataBoxLegendProps = useMemo(
    () => ({
      placement: 'top',
      baseLegendKeys: [incrementalLabel],
      lineKeys: [metricLabel, predictedLabel],
      keyColorMap: {
        [incrementalLabel]: '#8BEAB2AA',
      },
    }),
    [incrementalLabel, metricLabel, predictedLabel],
  );

  const xAxisFormatter = useGetDateAxisFormatter();

  const [infoOpen, setInfoOpen] = useState(false);

  const isLoading =
    sales.isLoading || decomps.isLoading || predictedSales.isLoading;
  const isError = sales.isError || decomps.isError || predictedSales.isError;

  const [modalIsOpen, setModalIsOpen] = useState(false);

  const yAxisFormatter = useChartEffectTickFormatter();

  const processedChartData: Array<Datapoint> = useMemo(() => {
    const salesData = getAggregateLevelData(sales, aggregateLevel);
    const predictedSalesData = getAggregateLevelData(
      predictedSales,
      aggregateLevel,
    );
    const decompsData = getAggregateLevelData(decomps, aggregateLevel);

    const dataByEpoch: Record<number, Datapoint> = {};

    const emptyPoint = (utcEpoch: number): Datapoint => ({
      utcEpoch,
      [metricLabel]: 0,
      [incrementalLabel]: 0,
      [predictedLabel]: 0,
    });

    salesData.forEach((s) => {
      dataByEpoch[s.utcEpoch] = {
        ...emptyPoint(s.utcEpoch),
        [metricLabel]: s.value,
      };
    });

    decompsData.forEach((d) => {
      if (!dataByEpoch[d.utcEpoch]) {
        dataByEpoch[d.utcEpoch] = emptyPoint(d.utcEpoch);
      }
      dataByEpoch[d.utcEpoch][incrementalLabel] = d.value;
    });

    predictedSalesData.forEach((d) => {
      if (!dataByEpoch[d.utcEpoch]) {
        dataByEpoch[d.utcEpoch] = emptyPoint(d.utcEpoch);
      }
      dataByEpoch[d.utcEpoch][predictedLabel] = d.value;
    });

    return Object.values(dataByEpoch).sort(sortEpoch);
  }, [
    sales,
    decomps,
    predictedSales,
    aggregateLevel,
    metricLabel,
    incrementalLabel,
    predictedLabel,
  ]);

  return (
    <>
      <InfoAlert
        headerText={props.title ?? ''}
        bodyText={`${incrementalMetricDef(metricLabel)}\n\n${observedMetricDef(metricLabel)}\n\n${predictedMetricDef(metricLabel)}`}
        isOpen={infoOpen}
        onClose={() => setInfoOpen(false)}
      />
      <DataBox
        {...props}
        onInfoButtonClick={() => setInfoOpen(true)}
        legendProps={legendProps}
        exportEnabled
        exportButtonEnabled={false}
        setTableDataModalIsOpen={setModalIsOpen}
        isError={isError}
        isLoading={isLoading}
        infoNextToTitle={true}
        hasData={processedChartData.length > 0}>
        <>
          <TimeseriesDataTableModal
            isOpen={modalIsOpen}
            setIsOpen={setModalIsOpen}
            yFormatter={yAxisFormatter}
            data={processedChartData}
            headerName={props.title ?? ''}
          />
          <NewBarLineGraph
            data={processedChartData}
            xAxisKey={xAxisKey}
            xAxisFormatter={xAxisFormatter}
            yAxisFormatter={yAxisFormatter}
            barLineIsGradient
          />
        </>
      </DataBox>
    </>
  );
};
